import React, { FC, useEffect, useState } from 'react';
import type { TableColumnsType } from 'antd';
import { Button, Col, Row, Table, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import EnvironmentSelect from '@lib/components/env-select';
import TaskState from '@lib/components/task/task-state';
import { DATE_TIME_FORMAT } from '@lib/utils/dayjs';

import { TaskStatus } from '@services/_api';

import { useArenaSegmentContext } from './components/context';
import CreateSegmentModal from './components/modal/create-segment';
import ForcePlayerModal from './components/modal/force-player';

dayjs.extend(relativeTime);
dayjs.extend(duration);

interface ArenaSegmentHistoryType {
  id: number;
  userEmail: string;
  data: any;
  createdAt: string;
  startTime: string;
  endTime: string;
  status: TaskStatus;
  taskType: string;
  environment: string;
  blueprintVersion: string;
  arenaSeasonId: string;
}

const { Text } = Typography;

const ArenaSegmentPageBody: FC = () => {
  const [isCreateSegmentModalVisible, setIsCreateSegmentModalVisible] = useState(false);
  const [isForcePlayerModalVisible, setIsForcePlayerModalVisible] = useState(false);
  const [timer, setTimer] = useState(0);
  const {
    environment,
    setEnvironment,
    segmentHistoryData,
    loadSegmemntHistory,
  } = useArenaSegmentContext();

  useEffect(() => {
    const updateTime = () => {
      setTimer((prevTimer) => prevTimer + 10);
    };
    const intervalId = setInterval(updateTime, 10000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (timer >= 10) {
      loadSegmemntHistory();
    }
  }, [timer]);

  useEffect(() => {
    loadSegmemntHistory();
  }, [environment]);

  const columns: TableColumnsType<ArenaSegmentHistoryType> = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
      sorter: true,
    },
    {
      title: 'User',
      dataIndex: 'userEmail',
      key: 'userEmail',
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      key: 'environment',
    },
    {
      title: 'Client Version',
      dataIndex: 'blueprintVersion',
      key: 'blueprintVersion',
    },
    {
      title: 'Task Type',
      dataIndex: 'taskType',
      key: 'taskType',
    },
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      width: 300,
      render: (_, record) => JSON.stringify(record.data),
    },
    {
      title: 'Register Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) =>
        record.createdAt ? (
          <Tooltip title={dayjs(record.createdAt).format(DATE_TIME_FORMAT)}>
            {dayjs(record.createdAt).fromNow()}
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (_, record) =>
        record.startTime ? (
          <Tooltip title={dayjs(record.startTime).format(DATE_TIME_FORMAT)}>
            {dayjs(record.startTime).fromNow()}
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      title: 'Duration',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (_, record) =>
        record.endTime
          ? dayjs
              .duration(dayjs(record.endTime).diff(dayjs(record.startTime), 'second'), 'second')
              .humanize(false)
          : '',
    },
    {
      title: 'Arena Season ID',
      dataIndex: 'arenaSeasonId',
      key: 'arenaSeasonId',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record: ArenaSegmentHistoryType) => (
        <TaskState data={record} value={record.status} />
      ),
    },
  ];

  return (
    <div className="flex flex-col flex-1 max-h-full min-h-full p-4">
      <Row justify="space-between" className="mb-4">
        <Col>
          <Text className="mr-2">Environment</Text>
          <EnvironmentSelect style={{ width: 200 }} onChange={setEnvironment} />
        </Col>
        <Col>
          <Button
            type="primary"
            className="mr-2"
            onClick={() => setIsCreateSegmentModalVisible(true)}
          >
            Create Segment
          </Button>
          <Button
            type="primary"
            danger
            className="mr-2"
            onClick={() => setIsForcePlayerModalVisible(true)}
          >
            Force Player
          </Button>
        </Col>
      </Row>
      <Table dataSource={segmentHistoryData ?? []} columns={columns} bordered />
      <CreateSegmentModal
        isOpen={isCreateSegmentModalVisible}
        setIsOpen={setIsCreateSegmentModalVisible}
      />
      <ForcePlayerModal
        isOpen={isForcePlayerModalVisible}
        setIsOpen={setIsForcePlayerModalVisible}
      />
    </div>
  );
};

export default ArenaSegmentPageBody;
